import type { 
  AggregateDataItem, 
  HeatmapDatum, 
  ChartConfig,
  AggregateByOptions,
  AggregateTwoDimensionsOptions,
  AggregateForChartOptions
} from "../types";
import { aggregateDateByYear, aggregateDateByMonth } from "./date-aggregation";
import { detectDateField, normalizeDataByDateField } from "./date-utils";

/**
 * Converts a string to a URL-friendly slug
 */
const toSlug = (str: string): string => 
  str.toLowerCase().replace(/\s+/g, "-");

/**
 * Generic aggregation function that groups data by a field and sums counts
 * 
 * @example
 * // Group tickets by site, counting 1 per ticket
 * aggregateBy({ data: tickets, groupBy: "asset_site", valuePrefix: "site" })
 * 
 * @example
 * // Group assignments by type, summing asset_count
 * aggregateBy({ data: assignments, groupBy: "assignee_type", countBy: "asset_count" })
 * 
 * @example
 * // Group with custom accessor
 * aggregateBy({ 
 *   data: items, 
 *   groupBy: (item) => item.date.substring(0, 7), // group by month
 *   countBy: (item) => item.quantity 
 * })
 */
export const aggregateBy = <T extends Record<string, any>>({
  data,
  groupBy,
  countBy,
  valuePrefix = "",
  sortDescending = true,
}: AggregateByOptions<T>): AggregateDataItem[] => {
  const getKey = typeof groupBy === "function"
    ? groupBy
    : (item: T) => String(item[groupBy] ?? "Unknown");

  const getCount = countBy === undefined
    ? () => 1
    : typeof countBy === "function"
      ? countBy
      : (item: T) => Number(item[countBy]) || 0;

  const map = new Map<string, number>();

  data.forEach((item) => {
    const key = getKey(item);
    const count = getCount(item);
    map.set(key, (map.get(key) || 0) + count);
  });

  const result = Array.from(map.entries()).map(([label, count]) => {
    const slug = toSlug(label);
    return {
      id: slug,
      value: valuePrefix ? `${valuePrefix}-${slug}` : slug,
      label,
      count,
    };
  });

  return sortDescending
    ? result.sort((a, b) => b.count - a.count)
    : result;
};

/**
 * Options for two-dimensional aggregation (e.g., site × type)
 */
/**
 * Aggregates data by two dimensions, creating heatmap-style data
 * Output: [{ site: "Central", Chromebook: 10, iPad: 5 }, ...]
 * 
 * @example
 * aggregateTwoDimensions({
 *   data: assets,
 *   primaryField: "asset_site",
 *   secondaryField: "asset_type",
 *   countBy: "count",
 *   primaryKeyName: "site"
 * })
 */
export const aggregateTwoDimensions = <T extends Record<string, any>>({
  data,
  primaryField,
  secondaryField,
  countBy,
  primaryKeyName = "site",
}: AggregateTwoDimensionsOptions<T>): HeatmapDatum[] => {
  const getPrimary = typeof primaryField === "function"
    ? primaryField
    : (item: T) => String(item[primaryField] ?? "Unknown");

  const getSecondary = typeof secondaryField === "function"
    ? secondaryField
    : (item: T) => String(item[secondaryField] ?? "Unknown");

  const getCount = countBy === undefined
    ? () => 1
    : typeof countBy === "function"
      ? countBy
      : (item: T) => Number(item[countBy]) || 0;

  const map = new Map<string, Map<string, number>>();

  data.forEach((item) => {
    const primary = getPrimary(item);
    const secondary = getSecondary(item);
    const count = getCount(item);

    if (!map.has(primary)) {
      map.set(primary, new Map());
    }
    const secondaryMap = map.get(primary)!;
    secondaryMap.set(secondary, (secondaryMap.get(secondary) || 0) + count);
  });

  return Array.from(map.entries()).map(([primary, secondaryMap]) => {
    const row: HeatmapDatum = { [primaryKeyName]: primary } as HeatmapDatum;
    secondaryMap.forEach((count, secondary) => {
      row[secondary] = count;
    });
    return row;
  });
};

/**
 * Extracts and normalizes raw API aggregate response into a consistent format.
 * Dynamically extracts the specified fields from the API response and normalizes
 * common fields like count and cost.
 * 
 * @param apiData - Raw API response
 * @param fieldsToExtract - Fields to extract (e.g., from config's groupBy fields)
 *                          If not provided, extracts all fields from the first item
 * @returns Normalized data array with count field parsed and cost field (if present) normalized
 * 
 * @example
 * // Asset report
 * const data = extractAndNormalizeReportData(apiResponse, ["asset_site", "type_asset"]);
 * 
 * @example
 * // Purchase report (with cost field)
 * const data = extractAndNormalizeReportData(apiResponse, ["type_asset", "purchase_date"]);
 */
export const extractAndNormalizeReportData = (
  apiData: Record<string, any>[],
  fieldsToExtract?: readonly string[]
): Record<string, any>[] => {
  if (apiData.length === 0) {
    return [];
  }

  // Determine which fields to extract
  const fieldsToUse = fieldsToExtract && fieldsToExtract.length > 0 
    ? fieldsToExtract 
    : (Object.keys(apiData[0]) as string[]);

  const extracted = apiData.map((item) => {
    const row: Record<string, any> = {};
    
    // Dynamically extract requested fields from the item
    fieldsToUse.forEach((field) => {
      const value = item[field];
      // Use the value if it exists; otherwise use empty string
      row[field] = value !== undefined ? value : "";
    });
    
    // Normalize count field if present
    if (item.count !== undefined) {
      row.count = Number(item.count ?? 1) || 1;
    }
    
    // Normalize cost field if present (for purchase reports)
    if (item.cost !== undefined) {
      row.cost = Number(item.cost ?? 0) || 0;
    }
    
    return row;
  });

  return extracted;
};

/**
 * Options for aggregating data for a chart based on its configuration.
 * Supports customizable count field, date aggregation, and export behavior.
 */
/**
 * Sorts aggregated chart data based on chart configuration.
 * 
 * Supports three sort orders:
 * - "alphabetical": Sorts by label alphabetically (A-Z), with "Unknown" at the end
 * - "high-to-low": Sorts by count descending (highest first)
 * - "low-to-high": Sorts by count ascending (lowest first)
 * 
 * Date-based charts (with dateAggregation) are typically not sorted by this function,
 * as they are sorted chronologically by default in their aggregation logic.
 * 
 * @param data - Array of aggregated data items
 * @param sortConfig - Sort configuration from chart config
 * @param isDateChart - Whether this is a date-based chart (should not be alphabetically sorted)
 * @returns Sorted data array
 * 
 * @example
 * // Alphabetical sort
 * sortChartData(barChartData, { order: "alphabetical" });
 * 
 * @example
 * // High to low by count
 * sortChartData(pieChartData, { order: "high-to-low" });
 */
export const sortChartData = (
  data: any[],
  sortConfig?: { order?: "alphabetical" | "high-to-low" | "low-to-high"; sortField?: string },
  isDateChart: boolean = false
): any[] => {
  if (!data || data.length === 0) {
    return data;
  }

  // Don't alphabetically sort date charts; they should maintain chronological order
  if (isDateChart && (!sortConfig || sortConfig.order === "alphabetical")) {
    return data;
  }

  const order = sortConfig?.order ?? "alphabetical";
  const sortField = sortConfig?.sortField ?? "count";

  // Helper to check if a value is "unknown" like
  const isUnknown = (label: string): boolean => {
    return label.toLowerCase() === "unknown" || 
           label === "" || 
           label === "null" ||
           label === "Unknown" ||
           label === "No Site Set";
  };

  const sorted = [...data].sort((a, b) => {
    const aLabel = a.label ?? a[sortField] ?? "";
    const bLabel = b.label ?? b[sortField] ?? "";
    
    const aIsUnknown = isUnknown(String(aLabel));
    const bIsUnknown = isUnknown(String(bLabel));

    // Always push "Unknown" to the end
    if (aIsUnknown && !bIsUnknown) return 1;
    if (!aIsUnknown && bIsUnknown) return -1;
    if (aIsUnknown && bIsUnknown) return 0;

    // Sort based on order
    if (order === "alphabetical") {
      return String(aLabel).localeCompare(String(bLabel));
    } else if (order === "high-to-low") {
      const aCount = Number(a.count ?? a[sortField] ?? 0);
      const bCount = Number(b.count ?? b[sortField] ?? 0);
      return bCount - aCount;
    } else if (order === "low-to-high") {
      const aCount = Number(a.count ?? a[sortField] ?? 0);
      const bCount = Number(b.count ?? b[sortField] ?? 0);
      return aCount - bCount;
    }

    return 0;
  });

  return sorted;
};

/**
 * Generates appropriate aggregation for a chart based on its config.
 * Works with any report type (assets-by-site, purchase-report, etc.) by
 * accepting customizable field names for counting/summing.
 * 
 * @param data - Raw report data from API (already normalized)
 * @param chart - Chart configuration with dimensions and type
 * @param options - Customization options (countBy, dateAggregationFuncs, exportField)
 * @returns Aggregated data matching the chart's specifications
 * 
 * @example
 * // Asset report (using "count" field)
 * aggregateForChart(data, config.charts[0]);
 * 
 * @example
 * // Purchase report (using "cost" field, with date aggregation)
 * aggregateForChart(data, config.charts[0], {
 *   countBy: "cost",
 *   dateAggregationFuncs: { byMonth: aggregateByMonth, byYear: aggregateByYear },
 *   exportField: "cost",
 * });
 */
export const aggregateForChart = (
  data: Record<string, any>[],
  chart: ChartConfig,
  options: AggregateForChartOptions = {}
): any[] => {
  const {
    countBy = "count",
    dateAggregationFuncs,
  } = options;
  const { dimensions, type } = chart;

  // Export table: return raw rows with only specified dimensions
  if (type === "export-table") {
    let dataToExport = data;
    
    // Resolve effective date aggregation:
    // - Use explicit config if present
    // - Otherwise, if any dimension is a date field, default to annual grouping
    const explicitDateAgg = (chart as any).dateAggregation as ["month" | "year", ...string[]] | undefined;
    const autoDetectedDateField = !explicitDateAgg
      ? detectDateField(dimensions, (chart as any).dateField, data)
      : undefined;
    const effectiveDateAgg: ["month" | "year", ...string[]] | undefined =
      explicitDateAgg ?? (autoDetectedDateField ? ["year"] : undefined);

    // Apply date aggregation if specified (explicit or auto-defaulted)
    if (effectiveDateAgg) {
      const dateField = explicitDateAgg
        ? detectDateField(dimensions, (chart as any).dateField, data)
        : autoDetectedDateField;
      
      if (dateField) {
        // Extract fields to sum from dateAggregation (everything after the granularity)
        const fieldsToSum = effectiveDateAgg.slice(1);
        
        // Filter dimensions to only include grouping dimensions (exclude fields to sum)
        const groupDimensions = (dimensions as string[]).filter(d => !fieldsToSum.includes(d));
        
        // Normalize the date field and aggregate by grouping dimensions
        dataToExport = normalizeDataByDateField(
          data,
          dateField,
          effectiveDateAgg,
          groupDimensions
        );
      }
    }
    
    return dataToExport.map((item) => {
      const row: Record<string, any> = {};
      (dimensions as string[]).forEach((field) => {
        row[field] = item[field];
      });
      return row;
    });
  }

  // Single dimension charts (bar, pie, line)
  if (dimensions.length === 1) {
    const dimension = dimensions[0] as string;

    // Special handling for date fields with date aggregation
    if ((chart as any).dateAggregation) {
      const dateAggArray = (chart as any).dateAggregation as [string, ...string[]];
      const dateAgg = dateAggArray[0] ?? "month";
      
      // Detect which field is the date field (explicit or auto-detect)
      const dateField = detectDateField(dimensions, (chart as any).dateField, data);
      
      if (!dateField) {
        // no date field found; fall through to standard aggregation
      } else {
        // Use generic date aggregation for the detected date field
        if (dateAgg === "year") {
          return aggregateDateByYear(data, dateField, countBy);
        } else if (dateAgg === "month") {
          return aggregateDateByMonth(data, dateField, countBy);
        }
      }
    }

    // Fall back to custom date aggregation functions if provided
    if (dateAggregationFuncs && (chart as any).dateAggregation) {
      const dateAggArray = (chart as any).dateAggregation as [string, ...string[]];
      const dateAgg = dateAggArray[0] ?? "month";
      const aggFunc = dateAgg === "year" ? dateAggregationFuncs.byYear : dateAggregationFuncs.byMonth;
      if (aggFunc) {
        return aggFunc(data);
      }
    }

    // Standard aggregation for any field
    const aggregated = aggregateBy({
      data,
      groupBy: dimension as any,
      countBy: countBy as any,
      sortDescending: countBy === "cost",
    });

    // Apply sorting based on chart configuration
    const isDateChart = (chart as any).isDateChart ?? (chart as any).dateAggregation ? true : false;
    return sortChartData(aggregated, (chart as any).sort, isDateChart);
  }

  // Two dimension charts (stacked-bar, heatmap)
  if (dimensions.length === 2) {
    const [primary, secondary] = dimensions as [string, string];

    // Stacked bar charts: normalize to ensure all secondary values in each row
    if (type === "stacked-bar") {
      // Pre-process data for date aggregation if needed
      let dataForAggregation = data;
      if ((chart as any).dateAggregation) {
        // Detect which field is the date field (explicit or auto-detect)
        const dateField = detectDateField(dimensions, (chart as any).dateField, data);
        
        if (!dateField) {
          // no date field found; skip date normalization
        } else {
          // Normalize date field using the consolidated helper function
          const dateAggArray = (chart as any).dateAggregation as ["month" | "year", ...string[]];
          dataForAggregation = normalizeDataByDateField(data, dateField, dateAggArray);
        }
      }

      const result = aggregateTwoDimensions({
        data: dataForAggregation,
        primaryField: primary,
        secondaryField: secondary,
        countBy: countBy as any,
      });

      // Get all unique secondary values to normalize rows
      const allSecondaryValues = Array.from(
        new Set(dataForAggregation.map((item) => item[secondary] as string).filter(Boolean))
      ).sort();

      // Normalize rows to include all secondary values
      const normalized = result.map((row) => {
        // Preserve the original primary field name from the result
        const normalized_row: Record<string, any> = { ...row };
        allSecondaryValues.forEach((val) => {
          if (!(val in normalized_row)) {
            normalized_row[val] = 0;
          }
        });
        return normalized_row;
      });

      // Sort by total count (or by primary key for date-based charts)
      const sortedResult = normalized
        .map((row) => {
          const primaryKeyName = Object.keys(row)[0]; // Get first key (usually the primary field)
          const total = Object.entries(row)
            .filter(([key]) => key !== primaryKeyName)
            .reduce((sum, [, val]) => sum + (typeof val === "number" ? val : 0), 0);
          return { ...row, _total: total, _primaryKey: primaryKeyName };
        })
        .sort((a: any, b: any) => {
          // For date-based charts, maintain chronological order
          if ((chart as any).dateAggregation) {
            const aKey = a[a._primaryKey];
            const bKey = b[b._primaryKey];
            return String(aKey).localeCompare(String(bKey));
          }
          
          // Apply custom sort configuration
          const sortConfig = (chart as any).sort;
          const order = sortConfig?.order ?? "alphabetical";
          const primaryKeyName = a._primaryKey;
          
          if (order === "alphabetical") {
            // Alphabetical sort by primary key, with "Unknown" at the end
            const aKey = String(a[primaryKeyName] ?? "");
            const bKey = String(b[primaryKeyName] ?? "");
            const aIsUnknown = aKey.toLowerCase() === "unknown" || aKey === "" || aKey === "null" || aKey === "No Site Set";
            const bIsUnknown = bKey.toLowerCase() === "unknown" || bKey === "" || bKey === "null" || bKey === "No Site Set";
            
            if (aIsUnknown && !bIsUnknown) return 1;
            if (!aIsUnknown && bIsUnknown) return -1;
            return aKey.localeCompare(bKey);
          } else if (order === "high-to-low") {
            return (b._total as number) - (a._total as number);
          } else if (order === "low-to-high") {
            return (a._total as number) - (b._total as number);
          }
          
          // Default: sort by total count descending
          return (b._total as number) - (a._total as number);
        })
        .map(({ _total, _primaryKey, ...row }: any) => row);
      
      return sortedResult;
    }

    // Heatmap charts: standardized format with row, column, value
    if (type === "heatmap") {
      const heatmapData: any[] = [];
      const dimensionMap = new Map<string, Map<string, number>>();

      data.forEach((item) => {
        const row = item[primary] as string;
        const col = item[secondary] as string;

        if (!row || !col) {
          return; // Skip empty rows or columns
        }

        if (!dimensionMap.has(row)) {
          dimensionMap.set(row, new Map());
        }

        const colMap = dimensionMap.get(row)!;
        const itemValue = item[countBy] ?? 0;
        const current = colMap.get(col) || 0;
        colMap.set(col, current + itemValue);
      });

      dimensionMap.forEach((colMap, row) => {
        colMap.forEach((value, col) => {
          // Use standardized field names "site" and "status" that HeatmapChart expects
          heatmapData.push({
            site: row,
            status: col,
            value,
          });
        });
      });

      return heatmapData;
    }

    // Multi-line charts: one line per secondary dimension
    if (type === "multi-line") {
      const secondaryMap = new Map<string, Map<string, number>>();

      // Pre-normalize data for date aggregation if needed
      let dataForAggregation = data;
      const dateField = (chart as any).dateAggregation ? detectDateField(dimensions, (chart as any).dateField, data) : undefined;
      if ((chart as any).dateAggregation && !dateField && (chart as any).type !== "export-table") {
        // no date field found; skip date normalization
      } else if ((chart as any).dateAggregation && dateField === primary) {
        // Normalize the date field using the consolidated helper function
        const dateAggArray = (chart as any).dateAggregation as ["month" | "year", ...string[]];
        dataForAggregation = normalizeDataByDateField(data, dateField, dateAggArray);
      }

      dataForAggregation.forEach((item) => {
        const lineId = item[secondary] as string; // The line identifier (e.g., site name)
        const xValue = item[primary] as string;   // The x-axis value (e.g., date, already normalized if needed)
        
        if (!lineId || !xValue) return;

        if (!secondaryMap.has(lineId)) {
          secondaryMap.set(lineId, new Map());
        }

        const lineData = secondaryMap.get(lineId)!;
        const itemValue = item[countBy] ?? 0;
        const current = lineData.get(xValue) || 0;
        lineData.set(xValue, current + itemValue);
      });

      // Convert to MultiLineSeriesData format
      return Array.from(secondaryMap.entries())
        .map(([lineName, xValues]) => ({
          id: lineName,
          data: Array.from(xValues.entries())
            .map(([x, y]) => ({ x, y: Number(y) as number }))
            .sort((a, b) => a.x.localeCompare(b.x)),
        }))
        .sort((a, b) => a.id.localeCompare(b.id));
    }
  }

  return [];
};
